import { afterRenderEffect, inject, Service, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { Authentification } from './authentification';
import { EtatService } from './etat';
import { TicketService } from './ticket';
import { SyntheseConnexionApi } from './synthese-connexion-api';
import {
  ChoixSynthese,
  SyntheseConnexion,
} from '../Composants/synthese-connexion/synthese-connexion';

/** Présente la synthèse après une connexion interactive, jamais à la restauration d’un JWT. */
@Service()
export class SyntheseConnexionService {
  private readonly document = inject(DOCUMENT);
  private readonly api = inject(SyntheseConnexionApi);
  readonly totalNouveautes = this.api.totalNouveautes;
  private sessionCompteur: number | undefined;
  private readonly auth = inject(Authentification);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly etat = inject(EtatService);
  private readonly tickets = inject(TicketService);
  private readonly navigation = signal(0);
  private readonly ouvertureApresConnexion = signal(false);
  private reference?: MatDialogRef<SyntheseConnexion, ChoixSynthese>;
  private detail?: MatDialogRef<unknown>;
  readonly erreurNavigation = signal('');

  constructor() {
    this.auth.sessionChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.api.reinitialiser();
      this.sessionCompteur = undefined;
      const precedente = this.reference;
      this.reference = undefined;
      precedente?.close();
      this.detail?.close();
      this.ouvertureApresConnexion.set(false);
      this.erreurNavigation.set('');
    });
    this.auth.connexionsReussies.pipe(takeUntilDestroyed()).subscribe(() => {
      this.ouvertureApresConnexion.set(true);
    });
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.detail?.close();
        this.navigation.update((v) => v + 1);
      });
    // Attendre la page authentifiée pour rendre le dialogue et restaurer le focus sur la cloche.
    afterRenderEffect(() => {
      this.navigation();
      const utilisateur = this.auth.tokenInfoSignal();
      let route = this.router.routerState.snapshot.root;
      while (route.firstChild) route = route.firstChild;
      if (!utilisateur || !this.router.navigated || route.data['blankLayout']) return;
      if (this.ouvertureApresConnexion()) {
        this.sessionCompteur = this.auth.sessionVersion;
        this.ouvrir();
      } else if (this.sessionCompteur !== this.auth.sessionVersion) {
        this.sessionCompteur = this.auth.sessionVersion;
        this.actualiserCompteur();
      }
    });
    afterRenderEffect((onCleanup) => {
      this.navigation();
      if (!this.auth.tokenInfoSignal()) return;
      let route = this.router.routerState.snapshot.root;
      while (route.firstChild) route = route.firstChild;
      if (!this.router.navigated || route.data['blankLayout']) return;
      const session = this.auth.sessionVersion;
      const actualiser = () => {
        if (session === this.auth.sessionVersion) this.actualiserCompteur();
      };
      const intervalle = setInterval(actualiser, 60_000);
      this.document.addEventListener('visibilitychange', actualiser);
      onCleanup(() => {
        clearInterval(intervalle);
        this.document.removeEventListener('visibilitychange', actualiser);
      });
    });
  }

  private actualiserCompteur(): void {
    if (!this.auth.tokenInfoSignal() || this.document.visibilityState !== 'visible') return;
    void this.api.charger().catch(() => undefined);
  }

  ouvrir(): void {
    const utilisateur = this.auth.tokenInfoSignal();
    if (!utilisateur || this.reference) return;
    this.ouvertureApresConnexion.set(false);
    this.erreurNavigation.set('');
    const session = this.auth.sessionVersion;
    const ref = this.dialog.open<SyntheseConnexion, { prenom: string }, ChoixSynthese>(
      SyntheseConnexion,
      {
        width: '680px',
        maxWidth: 'calc(100vw - 24px)',
        maxHeight: '90dvh',
        ariaLabelledBy: 'synthese-titre',
        autoFocus: '.croix',
        restoreFocus: '.notification-button',
        data: { prenom: utilisateur.prenom || utilisateur.username },
      },
    );
    this.reference = ref;
    ref.afterClosed().subscribe((choix) => {
      if (this.reference === ref) this.reference = undefined;
      if (choix && session === this.auth.sessionVersion) void this.naviguer(choix, session);
    });
  }

  private async naviguer(choix: ChoixSynthese, session: number): Promise<void> {
    try {
      let installationId: number | undefined;
      if ('ticket' in choix) {
        if (!choix.ticket.id) throw new Error('Identifiant de ticket absent');
        installationId = choix.ticket.installationId;
        if (!installationId) {
          const detail = await this.tickets.getTicket(choix.ticket.id);
          if (!detail) throw new Error('Ticket indisponible');
          installationId = Number(detail?.installation?.id ?? detail?.id_installation) || undefined;
        }
      }
      if (session !== this.auth.sessionVersion) return;
      const destination =
        'liste' in choix
          ? ['/tickets']
          : installationId
            ? ['/installation', installationId]
            : ['/parc'];
      const url = this.router.serializeUrl(this.router.createUrlTree(destination));
      if (this.router.url !== url && !(await this.router.navigate(destination)))
        throw new Error('Navigation refusée');
      if (session !== this.auth.sessionVersion || !('ticket' in choix)) return;
      const { TicketDetail } = await import('../Pages/ticket-detail/ticket-detail');
      if (session !== this.auth.sessionVersion || this.router.url !== url) return;
      this.etat.ticketSelectionne.set(choix.ticket);
      this.etat.siloEtatTicket.set(null);
      this.detail = this.dialog.open(TicketDetail, {
        width: '1280px',
        maxWidth: 'calc(100vw - 24px)',
        maxHeight: '92dvh',
        ariaLabelledBy: 'ticket-dialog-titre',
        autoFocus: '.fermer-ticket-dialog',
        restoreFocus: '.notification-button',
        data: { ticketId: choix.ticket.id },
      });
    } catch {
      if (session === this.auth.sessionVersion)
        this.erreurNavigation.set(
          'Impossible d’ouvrir le ticket ou sa liste. Réessayez depuis la cloche.',
        );
    }
  }
}
